import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const links = [
  { label: "Story", href: "#story" },
  { label: "Menu", href: "#menu" },
  { label: "Craft", href: "#craft" },
  { label: "Atmosphere", href: "#atmosphere" },
  { label: "Visit", href: "#visit" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleClick = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <header
        data-testid="site-nav"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-[#F9F9F6]/85 backdrop-blur-xl border-b border-black/5"
            : "bg-transparent"
        }`}
      >
        <div className="mx-auto max-w-[1500px] px-6 md:px-12 lg:px-16 flex items-center justify-between py-5 md:py-6">
          <button
            data-testid="nav-logo"
            onClick={() => handleClick("#top")}
            className="flex items-center gap-3 group"
          >
            <span
              className="font-display text-base md:text-lg text-ink"
              style={{ letterSpacing: "0.18em" }}
            >
              MATCHA<span className="text-matcha-dark">·</span>STORY
            </span>
          </button>

          <nav className="hidden md:flex items-center gap-10">
            {links.map((l) => (
              <button
                key={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                onClick={() => handleClick(l.href)}
                className="overline text-ink-soft hover:text-matcha-dark transition-colors duration-300"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <button
            data-testid="nav-visit-btn"
            onClick={() => handleClick("#visit")}
            className="hidden md:inline-flex items-center gap-2 rounded-full px-5 py-2.5 border border-ink/40 hover:border-ink hover:bg-ink hover:text-bone transition-all duration-300 text-xs uppercase tracking-[0.2em] font-medium"
          >
            Find Us
          </button>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((s) => !s)}
            className="md:hidden p-2 -mr-2 text-ink"
            aria-label="Open menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="fixed top-[68px] inset-x-0 z-40 md:hidden bg-bone border-b border-black/5"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <button
                  key={l.href}
                  data-testid={`mobile-link-${l.label.toLowerCase()}`}
                  onClick={() => handleClick(l.href)}
                  className="text-left font-serif text-3xl text-ink hover:text-matcha-dark transition-colors"
                >
                  {l.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
